import { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { formatPrice } from '@/lib/api';

/**
 * Upgrade common thumbnail URLs (Unsplash/Pexels) to a higher-resolution
 * variant so receipts render crisp on A4 / when printed.
 */
function hiResUrl(src) {
    if (!src || typeof src !== 'string') return src;
    try {
        // Unsplash: replace w= param
        if (src.includes('images.unsplash.com')) {
            return src.replace(/([?&])w=\d+/i, '$1w=1200').replace(/([?&])q=\d+/i, '$1q=80');
        }
        // Pexels: bump w param
        if (src.includes('images.pexels.com')) {
            return src.replace(/([?&])w=\d+/i, '$1w=1200');
        }
    } catch (_) {}
    return src;
}

/**
 * HD Graphical Receipt — rendered on-screen (off-canvas via ref)
 * and rasterised via html2canvas at 2x for crisp mobile viewing.
 *
 * Brand palette: Pink (#DB2777) / Black (#111) / White / Accent gray.
 */
const GraphicalReceipt = forwardRef(function GraphicalReceipt({ order }, ref) {
    if (!order) return null;
    const items = order.items || [];
    const addr = order.shipping_address || {};
    const date = order.created_at ? new Date(order.created_at) : new Date();
    const paid = (order.payment_status || '').toLowerCase() === 'paid';

    return (
        <div
            ref={ref}
            data-testid="graphical-receipt"
            style={{
                width: 720,
                background: '#ffffff',
                fontFamily: '"Outfit", Arial, sans-serif',
                color: '#111111',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Top Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #DB2777 0%, #FF1493 100%)',
                padding: '32px 40px 28px',
                color: '#fff',
                position: 'relative',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 32, fontWeight: 700, letterSpacing: -0.5, lineHeight: 1 }}>
                            Beauty<span style={{ color: '#FFD1E8' }}>By</span>Chancy
                        </div>
                        <div style={{ fontSize: 12, marginTop: 6, opacity: 0.9, letterSpacing: 0.3 }}>
                            GLOW BEAUTIFULLY, NATURALLY
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.85, marginBottom: 2 }}>INVOICE</div>
                        <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 24, fontWeight: 700 }}>
                            #{order.order_number}
                        </div>
                        <div style={{ fontSize: 11, marginTop: 4, opacity: 0.9 }}>
                            {date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Paid Watermark */}
            {paid && (
                <div style={{
                    position: 'absolute',
                    top: 280,
                    right: -40,
                    transform: 'rotate(-18deg)',
                    border: '6px solid #16a34a',
                    color: '#16a34a',
                    padding: '10px 40px',
                    fontSize: 44,
                    fontWeight: 800,
                    letterSpacing: 6,
                    borderRadius: 12,
                    opacity: 0.16,
                    pointerEvents: 'none',
                    fontFamily: '"Playfair Display", Georgia, serif',
                }}>
                    PAID
                </div>
            )}

            {/* Body */}
            <div style={{ padding: '28px 40px 20px' }}>
                {/* Ship To */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                    <div>
                        <div style={{ fontSize: 10, letterSpacing: 1.5, color: '#999', marginBottom: 6 }}>BILL / SHIP TO</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{addr.name || '—'}</div>
                        <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{addr.phone || ''}</div>
                        <div style={{ fontSize: 12, color: '#555', marginTop: 4, lineHeight: 1.4 }}>
                            {[addr.address, addr.city, addr.state].filter(Boolean).join(', ')}
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 10, letterSpacing: 1.5, color: '#999', marginBottom: 6 }}>PAYMENT STATUS</div>
                        <div style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            background: paid ? '#dcfce7' : '#fef3c7',
                            color: paid ? '#15803d' : '#b45309',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 600,
                            letterSpacing: 0.5,
                        }}>
                            {paid ? '● Paid' : '● Pending'}
                        </div>
                        <div style={{ fontSize: 10, letterSpacing: 1.5, color: '#999', marginTop: 12, marginBottom: 4 }}>ORDER STATUS</div>
                        <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize', color: '#111' }}>
                            {order.order_status || 'pending'}
                        </div>
                    </div>
                </div>

                {/* Items */}
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '68px 1fr 80px 130px',
                        gap: 14,
                        padding: '12px 14px',
                        background: '#fafafa',
                        fontSize: 11,
                        letterSpacing: 1.2,
                        color: '#888',
                        fontWeight: 600,
                    }}>
                        <span></span>
                        <span>ITEM</span>
                        <span style={{ textAlign: 'center' }}>QTY</span>
                        <span style={{ textAlign: 'right' }}>AMOUNT</span>
                    </div>
                    {items.map((it, i) => (
                        <div key={i} style={{
                            display: 'grid',
                            gridTemplateColumns: '68px 1fr 80px 130px',
                            gap: 14,
                            padding: '14px 14px',
                            borderTop: i === 0 ? 'none' : '1px solid #f5f5f5',
                            alignItems: 'center',
                        }}>
                            <div style={{
                                width: 60, height: 60, borderRadius: 10, overflow: 'hidden',
                                background: '#fce7f3',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid #f5dce7',
                            }}>
                                {it.image ? (
                                    <img alt="" src={hiResUrl(it.image)} crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover', imageRendering: 'auto' }} />
                                ) : (
                                    <span style={{ color: '#DB2777', fontSize: 22, fontWeight: 700 }}>B</span>
                                )}
                            </div>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 600, color: '#111', lineHeight: 1.3 }}>{it.name}</div>
                                {it.variant && <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>{it.variant}</div>}
                                <div style={{ fontSize: 11, color: '#999', marginTop: 3 }}>Unit: {formatPrice(it.price || 0)}</div>
                            </div>
                            <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#111' }}>×{it.quantity}</div>
                            <div style={{ textAlign: 'right', fontSize: 16, fontWeight: 700, color: '#111' }}>
                                {formatPrice((it.price || 0) * (it.quantity || 0))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Totals */}
                <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
                    {/* QR + Thank you note */}
                    <div style={{ flexShrink: 0, textAlign: 'center' }}>
                        <div style={{ padding: 6, background: '#fff', border: '1px solid #f1f1f1', borderRadius: 10, display: 'inline-block' }}>
                            <QRCodeSVG value="https://beautybychacy.com" size={76} level="M" bgColor="#ffffff" fgColor="#111111" />
                        </div>
                        <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>Scan · @beautybychancy</div>
                        <div style={{ fontSize: 12, fontFamily: '"Playfair Display", Georgia, serif', marginTop: 8, color: '#DB2777' }}>
                            Thank you for shopping with us
                        </div>
                    </div>
                    <div style={{ width: 280 }}>
                        <Row label="Subtotal" value={formatPrice(order.subtotal || 0)} />
                        {order.discount_amount > 0 && (
                            <Row label={`Discount${order.discount_code ? ` (${order.discount_code})` : ''}`} value={`- ${formatPrice(order.discount_amount)}`} />
                        )}
                        {order.shipping_fee > 0 && <Row label="Shipping" value={formatPrice(order.shipping_fee)} />}
                        {order.payment_method && String(order.payment_method).toLowerCase() !== 'split' && (
                            <Row label="Payment" value={String(order.payment_method).toUpperCase()} />
                        )}
                        {String(order.payment_method || '').toLowerCase() === 'split' && (order.split_payments || []).length > 0 && (
                            <div style={{ fontSize: 12, color: '#666', padding: '4px 0' }}>
                                <div style={{ marginBottom: 2 }}>Split payment:</div>
                                {(order.split_payments || []).map((sp, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 8 }}>
                                        <span>· {String(sp.method || '').toUpperCase()}</span>
                                        <span style={{ color: '#111' }}>{formatPrice(sp.amount || 0)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {order.staff_attendant_name && (
                            <Row label="Attendant" value={order.staff_attendant_name} />
                        )}
                        <div style={{ height: 1, background: '#DB2777', margin: '10px 0', opacity: 0.25 }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 15, color: '#111', fontWeight: 700 }}>Total</span>
                            <span style={{ fontSize: 28, color: '#DB2777', fontWeight: 800, fontFamily: '"Playfair Display", Georgia, serif' }}>
                                {formatPrice(order.total || 0)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div style={{
                background: '#111111',
                color: '#fff',
                padding: '18px 40px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 11,
            }}>
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>Thank you for shopping with us</div>
                    <div style={{ color: '#aaa' }}>Shop B308, Sheriff Plaza, Wuse 2, Abuja</div>
                </div>
                <div style={{ textAlign: 'right', color: '#aaa' }}>
                    <div>WhatsApp / Call: 07050657701</div>
                    <div>beautybychacy.com</div>
                </div>
            </div>
        </div>
    );
});

function Row({ label, value }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
            <span style={{ color: '#666' }}>{label}</span>
            <span style={{ color: '#111', fontWeight: 500 }}>{value}</span>
        </div>
    );
}

export default GraphicalReceipt;
